// English source strings for the wave-3 features (Inbox, integrations, incidents and pull
// requests, preview and browser tests, memory, Coolify test result). Merged into `en`.
export const enWave3 = {
  "common.opensNewTab": "(opens in a new tab)",
  "w3.endOfList": "{n} items — end of list.",
  "w3.reason": "Reason",
  "w3.reasonRequired": "Enter a reason; it is recorded in the audit log.",

  "err.blocked": "Blocked — not executed",
  "err.blockedBody": "The request was refused before anything ran. No browser was started and no target was contacted.",
  "err.decision": "Decision: {decision}",

  // project tabs
  "project.tab.preview": "Preview & tests",
  "project.tab.signals": "Incidents & PRs",
  "overview.previewManage": "Manage preview",

  // inbox
  "inbox.roleTitle": "The Inbox is limited to maintainers and administrators",
  "inbox.roleBody": "Raw external deliveries (GitHub, Sentry) can contain sensitive data, so only organization maintainers and administrators read them. Your role: {role}.",
  "inbox.roleHint": "Incidents and pull requests of your projects are visible to every role.",
  "inbox.seeIncidents": "See incidents",
  "inbox.deliveries": "Deliveries",
  "inbox.filters": "Inbox filters",
  "inbox.filter.state": "State",
  "inbox.filter.integration": "Integration",
  "inbox.filter.project": "Project",
  "inbox.filter.anyProject": "Any project",
  "inbox.filter.unassigned": "Unassigned (organization Inbox)",
  "inbox.untrustedNote": "Payloads are external, untrusted data: shown as text, never executed or treated as instructions.",
  "inbox.loadFailed": "Could not load the Inbox",
  "inbox.emptyTitle": "No deliveries yet",
  "inbox.emptyBody": "Deliveries appear here once an active GitHub or Sentry integration receives a webhook.",
  "inbox.emptyFilteredTitle": "No deliveries match these filters",
  "inbox.emptyFilteredBody": "Change or clear the filters to see other deliveries.",
  "inbox.col.received": "Received",
  "inbox.col.event": "Event",
  "inbox.col.state": "State",
  "inbox.col.project": "Project",
  "inbox.col.attempts": "Attempts",
  "inbox.unassigned": "Unassigned",
  "inbox.reprocessed": "reprocessed {n}×",
  "inbox.details": "Details",
  "inbox.openDetailFor": "Details of {event} delivery {id}",
  "inbox.detailTitle": "Delivery",
  "inbox.deliveryId": "Delivery id {id}",
  "inbox.detailFailed": "Could not load the delivery",
  "inbox.source": "Source",
  "inbox.occurred": "Occurred",
  "inbox.processed": "Processed",
  "inbox.nextAttempt": "Next attempt",
  "inbox.noNextAttempt": "None scheduled",
  "inbox.orderingKey": "Ordering key",
  "inbox.correlation": "Correlation id",
  "inbox.payloadHash": "Payload SHA-256",
  "inbox.deadLetter": "Dead letter",
  "inbox.lastError": "Last error",
  "inbox.reprocess": "Reprocess",
  "inbox.reprocessHint": "Queues the delivery again; the worker processes it with the current mappings.",
  "inbox.reprocessUnavailable": "Not available while the delivery is {state}: it is already queued or being processed.",
  "inbox.reprocessQueued": "Reprocessing requested.",
  "inbox.assign": "Assign to project",
  "inbox.assignTo": "Project",
  "inbox.assignHint": "The delivery is re-queued for that project. You need the maintainer role on it.",
  "inbox.assignRole": "You need the maintainer role on this project to assign deliveries to it.",
  "inbox.assignQueued": "Delivery assigned and re-queued.",
  "inbox.chooseProject": "Choose a project",
  "inbox.headers": "Stored headers (allowlisted)",
  "inbox.payload": "Payload",
  "inboxState.received": "Received",
  "inboxState.processing": "Processing",
  "inboxState.processed": "Processed",
  "inboxState.failed": "Failed",
  "inboxState.dead_letter": "Dead letter",
  "inboxState.ignored": "Ignored",
  "inboxSource.github": "GitHub",
  "inboxSource.sentry": "Sentry",

  // integrations
  "integrations.sections": "Integration sections",
  "integrations.tab.connections": "Connections",
  "integrations.tab.incidents": "Incidents",
  "integrations.tab.prs": "Pull requests",
  "integrations.roleTitle": "Connections are limited to maintainers and administrators",
  "integrations.roleBody": "Organization maintainers read integrations; administrators change them. Your role: {role}. Incidents and pull requests remain visible in the other tabs.",
  "integrations.blockedTitle": "Real calls need an explicit approval",
  "integrations.blockedBody": "Webhooks are received and verified. Sentry makes no real call. GitHub connections created with \"Connect GitHub\" reach GitHub only when the operator allows it; real work (pull requests, pushes) additionally needs the connection's id allowlisted and the connection active.",
  "integrations.readOnly": "Read-only: only administrators change integrations.",
  "integrations.connections": "Connections",
  "integrations.loadFailed": "Could not load integrations",
  "integrations.emptyTitle": "No integrations registered",
  "integrations.emptyBody": "An administrator registers the GitHub App or Sentry integration.",
  "integrations.emptyBodyAdmin": "Register a GitHub App or Sentry integration below. It starts as a draft and receives nothing until activated.",
  "integrationKind.github_app": "GitHub App",
  "integrationKind.sentry": "Sentry",
  "integrationKind.github_token": "GitHub token",
  "integrations.installation": "Installation",
  "integrations.notInstalled": "Not installed yet",
  "integrations.ownershipVerified": "Verified (installation token exchanged)",
  "integrations.tokenKind": "Token kind",
  "integrations.tokenClassic": "Classic (broad access)",
  "integrations.tokenFineGrained": "Fine-grained",
  "integrations.tokenExpires": "Expires",
  "integrations.lastTest": "Last test",
  "github.connectTitle": "Connect GitHub",
  "github.appTitle": "GitHub App (recommended)",
  "github.connectBody": "MaestrOS creates a private GitHub App on your account or organization. You approve it on GitHub, then install it on the repositories MaestrOS may use. Its keys go straight to the vault and are never shown again.",
  "github.permissions": "Permissions requested: read metadata; write code, pull requests and issues; read Actions. Changing workflows, checks and creating repositories stay off until a later decision (D21).",
  "github.ownerType": "Owner",
  "github.ownerUser": "My personal account",
  "github.ownerOrg": "An organization",
  "github.orgLogin": "Organization login",
  "github.orgLoginRequired": "Enter the organization login (letters, digits and hyphens).",
  "github.connectSubmit": "Continue on GitHub",
  "github.egressOffTitle": "GitHub is not reachable from this installation yet",
  "github.egressOff": "The connection steps call GitHub only after the operator allows it (github_setup in MAESTROS_ALLOW_REAL_INTEGRATIONS, a human decision). Until then they answer BLOCKED-external.",
  "github.tokenTitle": "Use a token instead",
  "github.tokenBody": "For accounts where you cannot create an App: paste a fine-grained personal access token of one owner. It goes to the vault and is never shown again.",
  "github.tokenOwner": "Owner login",
  "github.ownerLoginRequired": "Enter the GitHub login of the token owner.",
  "github.token": "Personal access token",
  "github.tokenHint": "Fine-grained token (github_pat_…) limited to the repositories MaestrOS may use.",
  "github.tokenRequired": "Paste the token.",
  "github.acceptClassic": "Accept a classic token (ghp_…)",
  "github.acceptClassicHint": "A classic token opens every repository its account can reach. Prefer a fine-grained token.",
  "github.tokenSubmit": "Save token",
  "github.tokenCreated": "Token saved. Run the test, then activate the connection.",
  "github.install": "Install on GitHub",
  "github.reinstall": "Manage installation",
  "github.result.ok": "Test passed",
  "github.result.failed": "Test failed",
  "github.repositoriesReachable": "{n} repositories reachable",
  "github.testSteps": "Test steps",
  "github.stepState.passed": "Passed",
  "github.stepState.failed": "Failed",
  "github.stepState.not_run": "Not run",
  "github.step.app_jwt": "App key signs a token",
  "github.step.installation": "Installation belongs to this App",
  "github.step.installation_token": "Installation token exchanged",
  "github.step.repository_access": "Repositories reachable",
  "github.step.token": "Token valid and not expired",
  "github.step.owner": "Token belongs to the owner",
  "github.callbackTitle": "Finishing the GitHub App",
  "github.callbackWorking": "Registering the App…",
  "github.callbackDone": "App {name} registered. Now install it on the repositories MaestrOS may use.",
  "github.missingParams": "This page is opened by GitHub's redirect. Start again from Integrations → Connections.",
  "github.backToIntegrations": "Back to integrations",
  "github.installedTitle": "Verifying the installation",
  "github.installedWorking": "Asking GitHub to confirm the installation…",
  "github.installRequested": "The installation was requested and waits for an organization owner's approval on GitHub. Come back once it is approved and install again from Integrations.",
  "github.installedDone": "Installation verified: {n} repositories reachable. Map them to projects, then activate the connection.",
  "integrationState.draft": "Draft",
  "integrationState.active": "Active",
  "integrationState.suspended": "Suspended",
  "integrationState.disabled": "Disabled",
  "integrations.githubTarget": "Installation target id",
  "integrations.githubTargetHint": "Numeric id sent in X-GitHub-Hook-Installation-Target-ID.",
  "integrations.githubTargetFormat": "Enter the numeric installation target id.",
  "integrations.sentryOrg": "Sentry organization slug",
  "integrations.sentryOrgHint": "Lowercase letters, digits, '-' or '_'.",
  "integrations.sentryOrgFormat": "Enter the organization slug (lowercase letters, digits, '-' or '_').",
  "integrations.appId": "App id",
  "integrations.botLogin": "Bot login",
  "integrations.botLoginHint": "The App's bot account (e.g. maestros[bot]); its own events are ignored as loops.",
  "integrations.allowedLogins": "Logins allowed to send comment commands",
  "integrations.allowedLoginsHint": "Comma-separated. Empty means nobody (default deny).",
  "integrations.ownership": "Ownership proof",
  "integrations.ownershipAttested": "Manual attestation recorded (unverified)",
  "integrations.ownershipMissing": "Missing",
  "integrations.ownershipStatement": "Ownership attestation",
  "integrations.ownershipHint": "Explain why this organization owns the installation target (at least 10 characters).",
  "integrations.ownershipUnverified": "A real proof needs a GitHub call and is BLOCKED-external: the attestation is recorded as unverified.",
  "integrations.realNetwork": "Real network",
  "integrations.allowlisted": "allowlisted for a future approved test",
  "integrations.notAllowlisted": "not allowlisted",
  "integrations.updated": "Updated",
  "integrations.webhookUrl": "Webhook URL for the operator",
  "integrations.webhookGithubHint": "Configure this URL in the GitHub App. Deliveries are routed by the installation target header ({target}); use the webhook secret stored below.",
  "integrations.webhookSentryHint": "Configure this URL in the Sentry integration. The route carries this integration's id ({id}); use the client secret stored below.",
  "integrations.copy": "Copy",
  "integrations.webhookCopied": "Webhook URL copied.",
  "integrations.copyFailed": "Could not copy; select the URL and copy it manually.",
  "integrations.secrets": "Secrets",
  "integrations.secretsWriteOnly": "Write-only: stored in the vault; only their state and a masked hint come back.",
  "integrations.webhookSecret": "Webhook secret",
  "integrations.sentryClientSecret": "Client secret",
  "integrations.appPrivateKey": "App private key",
  "integrations.keyHint": "Choose the .pem file; the key is never shown.",
  "integrations.keyLoaded": "Key loaded ({n} characters). It is sent once and never shown.",
  "integrations.setSecret": "Set",
  "integrations.replaceSecret": "Replace",
  "integrations.revokeSecret": "Revoke",
  "integrations.replaceTitle": "Store {label}",
  "integrations.revokeTitle": "Revoke {label}",
  "integrations.revokeBody": "The secret is revoked in the vault. Store a new one to use it again.",
  "integrations.revokeWebhookBody": "Without its webhook secret the integration cannot receive deliveries: an active or suspended integration returns to draft.",
  "integrations.secretRequired": "Enter the secret value.",
  "integrations.secretMin": "Webhook secrets need at least 16 characters.",
  "integrations.secretActivationHint": "Required before activation. At least 16 characters.",
  "integrations.secretStored": "Secret stored.",
  "integrations.secretRevoked": "Secret revoked.",
  "integrations.activate": "Activate",
  "integrations.reactivate": "Reactivate",
  "integrations.suspend": "Suspend",
  "integrations.disable": "Disable",
  "integrations.enable": "Enable",
  "integrations.test": "Test connection",
  "integrations.testResult": "Test result",
  "integrations.activateTitle": "Activate {name}",
  "integrations.suspendTitle": "Suspend {name}",
  "integrations.disableTitle": "Disable {name}",
  "integrations.activateBody": "The integration starts accepting deliveries; held deliveries are released. A stored webhook secret is required.",
  "integrations.suspendBody": "Deliveries are still recorded but held (not processed) until the integration is activated again.",
  "integrations.disableBody": "Deliveries are rejected. The configuration and history are kept.",
  "integrations.statementMin": "The attestation needs at least 10 characters.",
  "integrations.activationRefused": "Activation refused",
  "integrations.activationRefusedBody": "The server refused the activation. For security it does not say why (for example, the target may already be in use or the state changed). Reload and check the integration; contact an administrator if it persists.",
  "integrations.done.activate": "Integration activated.",
  "integrations.done.suspend": "Integration suspended.",
  "integrations.done.disable": "Integration disabled.",
  "integrations.editTitle": "Edit {name}",
  "integrations.refChangeHint": "Changing the target returns an active integration to draft: it needs a new ownership attestation and activation.",
  "integrations.saved": "Integration saved.",
  "integrations.register": "Register an integration",
  "integrations.registerSubmit": "Register as draft",
  "integrations.identity": "Identity",
  "integrations.kind": "Kind",
  "integrations.name": "Name",
  "integrations.nameRequired": "Enter a name.",
  "integrations.created": "Integration registered as draft.",
  "integrations.mappings": "Project mappings",
  "integrations.mappingGithubHint": "Maps a repository (owner/repo) to a project. Deliveries without a mapping stay in the organization Inbox.",
  "integrations.mappingSentryHint": "Maps a Sentry project (slug or id) to a project, optionally limited to environments.",
  "integrations.noMappings": "No mappings yet.",
  "integrations.repository": "Repository",
  "integrations.sentryProject": "Sentry project",
  "integrations.environments": "Environments",
  "integrations.allEnvironments": "All",
  "integrations.addMapping": "Add mapping",
  "integrations.mappingRequired": "Choose a project and enter the external reference.",
  "integrations.mappingCreated": "Mapping added.",
  "integrations.removeMapping": "Remove",
  "integrations.removeMappingTitle": "Remove mapping",
  "integrations.removeMappingBody": "Deliveries for {ref} will no longer be routed to this project.",
  "integrations.prDelivery": "Pull requests",
  "integrations.prDeliveryOffLabel": "Off",
  "integrations.prDeliveryRate": "On (up to {n}/h)",
  "integrations.prDeliveryEnable": "Turn PRs on",
  "integrations.prDeliveryDisable": "Turn PRs off",
  "integrations.prDeliveryTitle": "Turn pull request delivery on",
  "integrations.prDeliveryBody": "Approved tasks of this project will be pushed as real branches and pull requests to {ref} (up to {n} per hour), including those already awaiting release. Merging stays with you.",
  "integrations.prDeliveryOn": "Pull request delivery turned on.",
  "integrations.prDeliveryOff": "Pull request delivery turned off.",

  // incidents and pull requests
  "incidents.title": "Incidents",
  "incidents.filter.state": "State",
  "incidents.filter.project": "Project",
  "incidents.unassigned": "Without project",
  "incidents.loadFailed": "Could not load incidents",
  "incidents.emptyTitle": "No incidents",
  "incidents.emptyBody": "Sentry issues routed to your projects appear here once a Sentry integration is active.",
  "incidents.col.title": "Incident",
  "incidents.col.state": "State",
  "incidents.col.environment": "Environment",
  "incidents.col.events": "Events",
  "incidents.col.lastSeen": "Last seen",
  "incidents.col.task": "Task",
  "incidents.firstSeen": "first {when}",
  "incidents.openTask": "Open task",
  "incidents.noTask": "No task",
  "incidentState.open": "Open",
  "incidentState.linked": "Linked",
  "incidentState.resolved": "Resolved",
  "incidentState.ignored": "Ignored",
  "incidentState.regressed": "Regressed",
  "prs.title": "Pull requests",
  "prs.loadFailed": "Could not load pull requests",
  "prs.emptyTitle": "No pull requests linked",
  "prs.emptyBody": "Pull requests opened for tasks are linked here from GitHub deliveries.",
  "prs.col.pr": "Pull request",
  "prs.col.state": "State",
  "prs.col.branch": "Base branch",
  "prs.col.head": "Head",
  "prs.col.task": "Task",
  "prs.col.updated": "Updated",
  "prs.headSeq": "head #{n}",
  "prState.open": "Open",
  "prState.merged": "Merged",
  "prState.closed": "Closed",

  // preview & browser
  "browser.intro": "Preview targets, test accounts, scenarios and browser runs of this project. The browser executor is not installed yet (D11.1); runs and captures are refused and shown as such.",
  "preview.title": "Preview",
  "preview.loadFailed": "Could not load the preview",
  "preview.emptyTitle": "No preview target",
  "preview.emptyBody": "Register the URL of an environment of this project. A preview is not a production publication.",
  "preview.state.preparing": "Preparing",
  "preview.state.available": "Available",
  "preview.state.stale": "Stale",
  "preview.state.unavailable": "Unavailable",
  "preview.staleLabel": "Stale",
  "preview.stale.not_checked": "not checked yet",
  "preview.stale.candidate_differs": "the task has a newer candidate",
  "preview.stale.capture_without_candidate": "capture without a candidate",
  "preview.candidate": "Candidate",
  "preview.currentCandidate": "Current",
  "preview.candidateMatches": "matches the task's current candidate",
  "preview.candidateUnknown": "no task candidate to compare",
  "preview.checkedAt": "checked {when}",
  "preview.neverChecked": "never checked",
  "preview.frameTitle": "Live preview of {url}",
  "preview.noEmbedTitle": "Not embedded in the panel",
  "preview.policy.new_tab_only": "The target does not allow being framed by this panel; open it in a new tab.",
  "preview.policy.capture_only": "The target only allows viewing a capture.",
  "preview.policy.embeddable_blocked": "The target could be framed, but the preview is not fresh; open it in a new tab or view the capture.",
  "preview.embedReason": "Reason",
  "preview.embedSource": "Source",
  "preview.openNewTab": "Open in new tab",
  "preview.downloadCapture": "Download capture",
  "preview.captureFacts": "Capture from {when}",
  "preview.noCapture": "No capture yet.",
  "preview.noAlternatives": "No alternative is available for this target.",
  "preview.capture": "Capture",
  "preview.environment": "Environment",
  "preview.noEnvironment": "No environment",
  "preview.task": "Task",
  "preview.register": "Register a preview target",
  "preview.registerHint": "The URL must be allowed by the environment. The embed decision is recomputed by the server on every read.",
  "preview.url": "URL",
  "preview.urlFormat": "Enter an http(s) URL.",
  "preview.registerSubmit": "Register",
  "preview.registered": "Preview target registered.",
  "testAccounts.title": "Test accounts",
  "testAccounts.intro": "Credentials are write-only: stored in the vault, never shown or returned — only their state.",
  "testAccounts.empty": "No test accounts.",
  "testAccounts.label": "Label",
  "testAccounts.username": "Username",
  "testAccounts.hosts": "Allowed hosts",
  "testAccounts.hostsHint": "host, host:port or *.domain — comma-separated.",
  "testAccounts.hostsRequired": "Enter at least one allowed host.",
  "testAccounts.credentials": "Credentials",
  "testAccounts.password": "Password",
  "testAccounts.totp": "TOTP seed",
  "testAccounts.totpHint": "Base32 seed; write-only.",
  "testAccounts.writeOnly": "Write-only: sent once, never shown again.",
  "testAccounts.rotatePassword": "Rotate password",
  "testAccounts.rotateTotp": "Rotate TOTP",
  "testAccounts.setTotp": "Set TOTP",
  "testAccounts.revokeTotp": "Revoke TOTP",
  "testAccounts.revokeTotpBody": "The TOTP seed is revoked and every stored session of this account is invalidated.",
  "testAccounts.disable": "Disable",
  "testAccounts.disableTitle": "Disable {label}",
  "testAccounts.disableBody": "The account is disabled, its credentials are revoked in the vault and every session is invalidated. The record stays for audit.",
  "testAccounts.disableHint": "Disabling invalidates every stored session.",
  "testAccounts.add": "Add a test account",
  "testAccounts.identity": "Account",
  "testAccounts.chooseEnv": "Choose an environment",
  "testAccounts.noEnvironments": "This project has no environment yet; add one in the project settings.",
  "testAccounts.envRequired": "Choose an environment.",
  "testAccounts.required": "Required.",
  "testAccounts.create": "Add test account",
  "testAccounts.created": "Test account added.",
  "testAccounts.editTitle": "Edit {label}",
  "testAccounts.usernameChangeHint": "Changing the username invalidates every stored session.",
  "testAccounts.secretTitle": "Store {label}",
  "testAccounts.secretStored": "Credential stored.",
  "testAccounts.sessionsRevokedHint": "Storing a new credential invalidates every stored session of this account.",
  "sessions.title": "Browser sessions",
  "sessions.empty": "No stored sessions.",
  "sessions.storedIn": "storage state: {state} (vault)",
  "sessions.obtained": "obtained {when}",
  "sessions.revoke": "Revoke",
  "sessionState.valid": "Valid",
  "sessionState.expired": "Expired",
  "sessionState.revoked": "Revoked",
  "sessionState.invalid": "Invalid",
  "scenarios.title": "Scenarios",
  "scenarios.empty": "No scenarios.",
  "scenarios.key": "Scenario",
  "scenarios.account": "Test account",
  "scenarios.noAccount": "No account",
  "scenarios.hash": "Body hash",
  "scenarios.run": "Run",
  "scenarios.activate": "Activate",
  "scenarios.retire": "Retire",
  "scenarios.runResult": "Run of {key}",
  "scenarios.runAccepted": "Run accepted; it appears in the list below.",
  "scenarios.add": "Add a scenario",
  "scenarios.body": "Scenario (maestros.browser.scenario.v1)",
  "scenarios.bodyHint": "Declarative JSON: fixed steps, no scripting, no literal secrets (use fill_secret). The version is assigned by the server.",
  "scenarios.bodyJson": "The scenario is not valid JSON.",
  "scenarios.bodyObject": "The scenario must be a JSON object.",
  "scenarios.create": "Save as draft",
  "scenarios.created": "Scenario saved as draft.",
  "scenarioState.draft": "Draft",
  "scenarioState.active": "Active",
  "scenarioState.retired": "Retired",
  "browserRuns.title": "Browser runs",
  "browserRuns.blockedNote": "The browser executor is not installed (D11.1) and staging runs need human authorization (D11.2): new runs are refused with a BLOCKED result.",
  "browserRuns.emptyTitle": "No browser runs",
  "browserRuns.emptyBody": "Runs appear here once the executor is authorized and a scenario runs.",
  "browserRuns.scenario": "Scenario",
  "browserRuns.status": "Status",
  "browserRuns.mode": "Mode",
  "browserRuns.blocked": "Blocked requests",
  "browserRuns.blockedCount": "{n} blocked",
  "browserRuns.created": "Created",
  "browserRuns.trace": "Trace",
  "browserRuns.downloadTrace": "Download trace",
  "runStatus.queued": "Queued",
  "runStatus.running": "Running",
  "runStatus.passed": "Passed",
  "runStatus.failed": "Failed",
  "runStatus.aborted": "Aborted",
  "runStatus.paused": "Paused",
  "runStatus.rejected": "Rejected",
  "runMode.local_fixture": "Local fixture",
  "runMode.mock": "Mocked services",
  "runMode.staging": "Staging",

  // memory
  "memory.projectOnly": "Memory belongs to this project only; nothing is shared with or retrieved from other projects.",
  "memory.project": "Project",
  "memory.projectHint": "Memory is per project.",
  "memory.openInProject": "Open in the project",
  "memory.noProjectsTitle": "No project yet",
  "memory.noProjectsBody": "Memory is kept per project. Create a project to start its memory.",
  "memory.sections": "Memory sections",
  "memory.tab.search": "Search",
  "memory.tab.sources": "Sources",
  "memory.tab.index": "Index & evaluations",
  "memory.tab.stats": "Statistics",
  "memory.searchTitle": "Search memory",
  "memory.query": "Question or keywords",
  "memory.queryRequired": "Enter what to search for.",
  "memory.k": "Results (k)",
  "memory.strategy": "Strategy",
  "memory.strategyHint": "Vector and rerank strategies need providers that are BLOCKED-external.",
  "memory.strategyDefault": "Index default",
  "memory.includeHistorical": "Include historical versions",
  "memory.search": "Search",
  "memory.resultMeta": "{n} results · {strategy} · {latency}",
  "memory.degradedNotice": "Semantic search unavailable — showing text results (embedding or reranking failed).",
  "memory.indexVersion": "index",
  "memory.eligibleChunks": "{n} eligible chunks",
  "memory.noHits": "Nothing relevant found",
  "memory.noHitsBody": "No current source matches. Try other words or include historical versions.",
  "memory.score": "score {score}",
  "memory.provenance": "Provenance",
  "memory.sourceRef": "Source",
  "memory.commit": "Commit",
  "memory.author": "Author",
  "memory.classification": "Classification",
  "memory.lines": "Lines",
  "memory.scores": "Scores",
  "memory.symbols": "Symbols",
  "memory.supersededBy": "Superseded by",
  "memory.current": "Current",
  "memory.historical": "Historical",
  "memory.sourcesTitle": "Sources",
  "memory.filter.state": "State",
  "memory.filter.kind": "Kind",
  "memory.filter.versions": "Versions",
  "memory.filter.currentOnly": "Current only",
  "memory.filter.historicalOnly": "Historical only",
  "memory.filter.allVersions": "All versions",
  "memory.sourcesFailed": "Could not load memory sources",
  "memory.noSourcesTitle": "No sources",
  "memory.noSourcesBody": "A maintainer adds decisions, PRD versions or repository files to this project's memory.",
  "memory.noSourcesBodyMaintainer": "Add a decision, a PRD version or repository files below.",
  "memory.col.source": "Source",
  "memory.col.state": "State",
  "memory.col.origin": "Author / commit",
  "memory.col.created": "Created",
  "memory.versions": "Versions",
  "memory.versionHistory": "Version history",
  "memory.contentHash": "Content hash",
  "memory.reject": "Reject",
  "memory.rejectTitle": "Reject {key}",
  "memory.rejectBody": "The version stays in the history but is no longer retrieved as knowledge.",
  "memory.tombstone": "Forget (tombstone)",
  "memory.tombstoneTitle": "Forget {key}",
  "memory.tombstoneBody": "Removes every version, chunk and embedding of this source and records a tombstone so it is not ingested again. This cannot be undone.",
  "memory.tombstoned": "{key} forgotten",
  "memory.tombstoneCounts": "{versions} versions, {chunks} chunks, {embeddings} embeddings and {retrievals} retrieval records removed.",
  "memory.ingestTitle": "Add to memory",
  "memory.ingestHint": "Unvalidated knowledge enters as a candidate, never automatically as a fact.",
  "memory.origin": "Origin",
  "memory.origin.decision": "Decision",
  "memory.origin.prd_version": "PRD version",
  "memory.origin.repository": "Repository files",
  "memory.decisionKey": "Key",
  "memory.decisionKeyHint": "Stable id (lowercase, digits, '.', '_' or '-'); new text for the same key creates a new version.",
  "memory.decisionTitle": "Title",
  "memory.decisionBody": "Decision",
  "memory.evidence": "Evidence references",
  "memory.evidenceHint": "One per line (ADR, PR, decision log entry). Required for a validated decision.",
  "memory.requestValidated": "Request validated state",
  "memory.validatedHint": "Honored only for a signed-in maintainer with at least one evidence reference.",
  "memory.prdVersion": "PRD version",
  "memory.choosePrdVersion": "Choose a PRD version",
  "memory.noPrdVersions": "This project has no PRD version yet.",
  "memory.workspace": "Workspace",
  "memory.chooseWorkspace": "Choose a workspace",
  "memory.noWorkspaces": "This project has no workspace.",
  "memory.paths": "Paths",
  "memory.pathsHint": "One per line, relative to the workspace. Read-only git access at the given commit.",
  "memory.ingest": "Add",
  "memory.ingested": "Memory updated.",
  "memory.ingestResult": "Result per source",
  "memory.outcome": "Outcome",
  "memory.detail": "Detail",
  "memory.chunks": "{n} chunks",
  "memOutcome.created": "Created",
  "memOutcome.unchanged": "Unchanged",
  "memOutcome.refused": "Refused",
  "memKind.markdown": "Markdown",
  "memKind.prd": "PRD",
  "memKind.code": "Code",
  "memKind.text": "Text",
  "memState.candidate": "Candidate",
  "memState.validated": "Validated",
  "memState.obsolete": "Obsolete",
  "memState.rejected": "Rejected",
  "memory.statsTitle": "Statistics",
  "memory.statsSources": "Sources",
  "memory.statsIndex": "Active index",
  "memory.statsRetrievals": "Retrievals",
  "memory.stat.current": "Current sources",
  "memory.stat.historical": "Historical versions",
  "memory.stat.withoutEvidence": "Without evidence",
  "memory.stat.obsolete": "Obsolete",
  "memory.stat.rejected": "Rejected",
  "memory.stat.tombstoned": "Forgotten keys",
  "memory.stat.oldest": "oldest current: {when}",
  "memory.stat.newest": "newest: {when}",
  "memory.stat.chunks": "Chunks",
  "memory.stat.bytes": "Content bytes",
  "memory.stat.duplicates": "Duplicate groups",
  "memory.stat.conflicts": "Conflicting symbols",
  "memory.stat.retrievals": "Retrievals",
  "memory.stat.empty": "Without results",
  "memory.stat.latencyMean": "Mean latency",
  "memory.stat.latencyP95": "p95 latency",
  "memory.stat.tokens": "Inserted tokens (estimate)",
  "memory.stat.cost": "Retrieval cost",
  "memory.noActiveIndex": "No active index version: search is unavailable until one is built, evaluated and activated.",
  "memory.unknown": "unknown",
  "memIndex.title": "Index versions",
  "memIndex.intro": "Build a version, evaluate it on a dataset, compare it with the active one, then activate it. Activation checks which version is active now; if it changed, reload.",
  "memIndex.serving": "Serving",
  "memIndex.noneServing": "No version serving",
  "memIndex.emptyTitle": "No index version",
  "memIndex.emptyBody": "A maintainer builds the first version (text strategy has no provider cost).",
  "memIndex.version": "Version",
  "memIndex.status": "Status",
  "memIndex.dates": "Dates",
  "memIndex.chunking": "chunking {v}",
  "memIndex.servingBadge": "serving",
  "memIndex.noEmbedder": "no embedder",
  "memIndex.createdAt": "created {when}",
  "memIndex.activatedAt": "activated {when}",
  "memIndex.retiredAt": "retired {when}",
  "memIndex.building": "Building",
  "memIndex.ready": "Ready",
  "memIndex.active": "Active",
  "memIndex.retired": "Retired",
  "memIndex.failed": "Failed",
  "memIndex.evaluate": "Evaluate",
  "memIndex.compare": "Compare",
  "memIndex.activate": "Activate",
  "memIndex.retire": "Retire",
  "memIndex.needDataset": "Create a dataset first.",
  "memIndex.buildStrategy": "Strategy",
  "memIndex.buildHint": "Only text works now; embedding and rerank providers are BLOCKED-external.",
  "memIndex.build": "Build version",
  "memIndex.built": "Index version built.",
  "memIndex.activated": "Index version activated.",
  "memIndex.rollback": "Roll back",
  "memIndex.rolledBack": "Rolled back to the previous version.",
  "memIndex.retireTitle": "Retire index version",
  "memIndex.retireBody": "The version stops being searchable and cannot be activated again.",
  "memIndex.evaluateTitle": "Evaluate index version",
  "memIndex.compareTitle": "Compare with baseline",
  "memIndex.baseline": "Baseline",
  "memIndex.baselineActive": "Active version",
  "memIndex.target": "This version",
  "memIndex.delta": "Change",
  "memIndex.promotable": "Can be promoted",
  "memIndex.promotableBody": "The lifecycle rules allow activating this version.",
  "memIndex.notPromotable": "Cannot be promoted yet",
  "memDataset.title": "Evaluation datasets",
  "memDataset.empty": "No datasets.",
  "memDataset.name": "Name",
  "memDataset.cases": "Cases",
  "memDataset.hash": "Body hash",
  "memDataset.freeze": "Freeze",
  "memDataset.retire": "Retire",
  "memDataset.draft": "Draft",
  "memDataset.frozen": "Frozen",
  "memDataset.retired": "Retired",
  "memDataset.add": "Create a dataset",
  "memDataset.casesLabel": "Cases (JSON)",
  "memDataset.casesHint": "Array of {case_key, question, expected[{source_key, symbol?}], forbidden_source_keys, stale_source_keys}. Empty expected = negative question.",
  "memDataset.casesJson": "The cases are not valid JSON.",
  "memDataset.casesArray": "Enter a non-empty JSON array of cases.",
  "memDataset.nameRequired": "Enter a name.",
  "memDataset.create": "Create dataset",
  "memDataset.created": "Dataset created.",
  "memEval.title": "Evaluations",
  "memEval.dataset": "Dataset",
  "memEval.runActive": "Evaluate active version",
  "memEval.againstActive": "Runs against the active index version.",
  "memEval.noActive": "No active version: evaluate a specific version from the list above.",
  "memEval.running": "Evaluating…",
  "memEval.failed": "The evaluation failed",
  "memEval.empty": "No evaluations yet.",
  "memEval.metric": "Metric",
  "memEval.recall": "Recall@k",
  "memEval.contamination": "Contamination",
  "memEval.staleAsCurrent": "Stale as current",
  "memEval.reportHash": "Report hash",
  "memEvalState.running": "Running",
  "memEvalState.completed": "Completed",
  "memEvalState.failed": "Failed",

  // settings → infrastructure
  "infra.coolifyReadOnly": "read-only test",
} as const;
